import { type ReactElement } from "react";
import Typography from "@mui/material/Typography";
import {
  Box,
  Button,
  TextField,
  type InputBaseComponentProps,
} from "@mui/material";
import {
  NYHalfWidthNumberToFillWidthNumber,
  convertCirclesToZero,
} from "@/utils/utils";
import {
  normalizeSearchText,
  sliceGraphemes,
} from "@/features/map/utils/sanitizeSearchText";
import { MAP_BUTTON_HEIGHT } from "@/features/map/constants/controlSize";

interface Props {
  label: string;
  /** 入力欄が空の時に出す例。省略時は label */
  placeholder?: string;
  handleApiRequest: () => Promise<void>;
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  disabled: boolean;
  inputProps?: InputBaseComponentProps;
}

export const SearchBox = ({
  label,
  placeholder,
  handleApiRequest,
  searchTerm,
  setSearchTerm,
  disabled,
  inputProps,
}: Props): ReactElement<Props> => {
  // 入力の上限（maxLength）は見た目の文字数で数える
  // （絵文字＝サロゲートペアや結合文字を String.length のように過大カウントしない）。
  // 文字数のカウンタは出さず、上限を超える分を切り詰めるだけにする。
  // inputProps は any を含む型のため、number に絞り込んでから利用する。
  const maxLength =
    typeof inputProps?.maxLength === "number"
      ? inputProps.maxLength
      : undefined;
  // native maxLength は UTF-16 コードユニット基準で絵文字（サロゲートペア）を 2 文字と数えるため、
  // 見た目の文字数より早く入力が止まる。単位を一致させるため
  // native maxLength は TextField へ渡さず、onChange/onBlur で見た目の文字数に切り詰めて制限する。
  const { maxLength: _maxLength, ...restInputProps } = inputProps ?? {};
  // maxLength 指定時のみ見た目の文字数で上限まで切り詰める。未指定なら入力をそのまま通す。
  const limitToMaxLength = (value: string): string =>
    maxLength !== undefined ? sliceGraphemes(value, maxLength) : value;

  // 地図画面の左列に収まるよう、ラベルの下に入力欄と検索ボタンを1行に並べる
  return (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
        {label}
      </Typography>
      <Box sx={{ display: "flex", gap: 1 }}>
        <TextField
          id="chiban"
          // ラベルは上の行に出しているので、入力欄の中には例だけを出す（読み上げ用に aria-label を付ける）
          placeholder={placeholder ?? label}
          size="small"
          fullWidth
          // 任意入力のため、入口で制御文字除去 + NFC 正規化してから state に反映する。
          // NFC 合成は IME 変換中の文字を変えない（半角→全角変換と異なり確定を誘発しない）ため onChange で行う。
          // URL パラメータ経由は urlParams/schema.ts で同じ正規化を行う。
          onChange={(event) => {
            setSearchTerm(
              limitToMaxLength(normalizeSearchText(event.target.value))
            );
          }}
          onBlur={(event) => {
            // onChange に変換処理を入れると、入力中の変換が自動的に確定されてしまうため、
            // onBlur に変換処理を入れる
            const result = NYHalfWidthNumberToFillWidthNumber(
              convertCirclesToZero(normalizeSearchText(event.target.value))
            );
            setSearchTerm(limitToMaxLength(result));
          }}
          value={searchTerm}
          autoFocus={false}
          disabled={disabled}
          inputProps={{ ...restInputProps, "aria-label": label }}
          // 地図画面のボタン（PC は 32px・SP はタップしやすい高さ、小さめの文字）とそろえる
          sx={{
            "& .MuiInputBase-root": { height: MAP_BUTTON_HEIGHT },
            "& .MuiInputBase-input": { fontSize: "0.875rem" },
          }}
        />
        {/* 高さは入力欄に合わせて伸ばす */}
        <Button
          variant="contained"
          onClick={() => {
            void handleApiRequest();
          }}
          disabled={disabled}
          sx={{ flexShrink: 0, minWidth: 64, px: 2 }}
        >
          検索
        </Button>
      </Box>
    </Box>
  );
};
